import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { useStudentAcademicSummary } from './useStudentAcademicSummary';

interface Props {
  schoolId: string;
  schoolYearId: string;
  personId: string;
  title: string;
}

export function StudentAcademicSummaryPanel({ schoolId, schoolYearId, personId, title }: Props) {
  const { data, loading, error } = useStudentAcademicSummary(schoolId, schoolYearId, personId);
  const summary = data?.studentAcademicSummary;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>Current grades from posted work and attendance so far this year. Grades are not final until the term is finalized.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">Unable to load grades: {error.message}</div>}
        {summary && (
          <>
            {summary.classGrades.length === 0 ? (
              <p className="text-sm text-muted-foreground">No graded classes yet.</p>
            ) : (
              <div className="divide-y rounded-md border">
                {summary.classGrades.map((grade) => (
                  <div key={`${grade.classOffering.id}-${grade.transcriptName}`} className="flex flex-wrap items-center justify-between gap-3 p-3">
                    <div>
                      <p className="font-medium">{grade.transcriptName}</p>
                      <p className="text-sm text-muted-foreground">{grade.classOffering.displayName}</p>
                    </div>
                    <div className="text-right">
                      {grade.result.percentage != null ? (
                        <>
                          <p className="font-semibold tabular-nums">{grade.result.percentage}%{grade.result.letterGrade ? ` · ${grade.result.letterGrade}` : ''}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">{grade.result.earnedPoints}/{grade.result.possiblePoints} pts · {grade.result.includedAssignmentCount} graded</p>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">Nothing graded yet</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-md border p-3">
              <p className="text-sm font-medium">Attendance</p>
              {summary.attendance.recordedCount === 0 ? (
                <p className="text-sm text-muted-foreground">No attendance recorded yet.</p>
              ) : (
                <p className="text-sm text-muted-foreground tabular-nums">
                  {summary.attendance.presentCount} present · {summary.attendance.absentCount} absent · {summary.attendance.tardyCount} tardy · {summary.attendance.excusedCount} excused
                  {summary.attendance.attendanceRate != null ? ` · ${summary.attendance.attendanceRate}% attendance` : ''}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
